import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { printLatestDate } from '@/components/NCCard/NCCard';
import {
  Explainer,
  ExplainerBody,
  ExplainerContent,
  ExplainerCopy,
  ExplainerTitle,
  ExplainerTrigger,
} from '@/components/NCExplainers/Explainer';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { PlotIndicatorCard } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotIndicatorCard';
import { isNullish } from '@/utils/booleans';

type NaturePositiveIndexProps = {
  data: PlotReportFactElement<number>;
};
export const NaturePositiveIndex = (props: NaturePositiveIndexProps) => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.naturePositiveIndex.labels.heading')}>
      <NaturePositiveIndexCard {...props} />
    </PlotCardWrapper>
  );
};

const NaturePositiveIndexCard = ({ data }: NaturePositiveIndexProps) => {
  const { t } = useTranslation();

  const hasData = !data || isNullish(data.value) || typeof data.value !== 'number';
  if (hasData) {
    return null;
  }

  const cardProps = {
    'data-testid': 'index-card-nca',
    explainer: <NaturePositiveIndexExplainer />,
  };
  const headerProps = {
    title: t(`shared.ncaDetail.details.naturePositiveIndex.labels.heading`),
    className: 'lg:grid-cols-[1fr_1fr]',
  };
  const footerProps = {
    logos: ['tnfd', 'sbtn', 'gbf', 'cdp', 'csrd', 'npi', 'eudr', 'sai'],
  };

  const lastMeasuredDate = printLatestDate([data?.measured_at]);
  const factLinearGradientProps = {
    latestFact: data,
    ncFactCapsuleProps: {
      label: t('shared.ncaDetail.details.labels.plotAvgValue'),
      infoPopoverProps: {
        title: t('shared.ncaDetail.details.plotAvgValue.title'),
        body: t('shared.ncaDetail.details.plotAvgValue.body', {
          date: lastMeasuredDate,
        }),
        popoverTriggerProps: {
          className: 'text-xs',
          'data-testid': 'eii-mean-tooltip',
        },
      },
    },
  };
  const graphHeaderProps = {
    heading: t(`shared.ncaDetail.details.naturePositiveIndex.labels.heading`),
    popover: {
      title: t('shared.ncaDetail.details.naturePositiveIndex.explainer.naturePositiveIndex.title'),
      body: t('shared.ncaDetail.details.naturePositiveIndex.explainer.naturePositiveIndex.body'),
    },
    className: 'w-full px-6 sm:px-0 justify-center',
  };

  const graphProps = {
    factLinearGradientProps,
    graphHeaderProps,
    className: 'justify-self-center',
  };

  return (
    <PlotIndicatorCard
      cardProps={cardProps}
      headerProps={headerProps}
      footerProps={footerProps}
      graphProps={graphProps}
      nCCard2SectionLayoutProps={{
        showDivider: false,
      }}
    />
  );
};

const NaturePositiveIndexExplainer = () => {
  const { t } = useTranslation();

  return (
    <Explainer>
      <ExplainerTrigger data-testid='nature-positive-index-explainer'>
        {t('global.howDoWeMeasureThis')}
      </ExplainerTrigger>
      <ExplainerContent>
        <ExplainerTitle>
          {t(`shared.ncaDetail.details.naturePositiveIndex.explainer.howDoWeMeasureThis.title`)}
        </ExplainerTitle>
        <Stack className='items-start pb-6'>
          <ExplainerBody>
            <ExplainerCopy body={t(`shared.ncaDetail.details.naturePositiveIndex.explainer.howDoWeMeasureThis.body`)} />
          </ExplainerBody>
          {/* TODO: MVP-9692 update methodology link to correct page when available */}
          {/* <MethodologyLink to={''} /> */}
        </Stack>
      </ExplainerContent>
    </Explainer>
  );
};
